import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Brain, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { quotesService, type Quote as QuoteType } from "@/services/quotesService";
import { contentPrefetchService } from "@/services/contentPrefetchService";
import { Vital7Dashboard } from "@/components/Vital7Dashboard";
import { GamificationHub } from "@/components/GamificationHub";
import { BrainBuilderWorkout } from "@/components/BrainBuilderWorkout";
import { LearningQueue } from "@/components/LearningQueue";
import { ContinueWhereYouLeftOffCard } from "@/components/ContinueWhereYouLeftOffCard";
import { HomeQuoteCard } from "@/components/HomeQuoteCard";
import { HomeCustomizeMenu } from "@/components/HomeCustomizeMenu";
import { ChatWorkspace } from "@/pages/ChatWorkspace";
import { useHomeCardsPreferences } from "@/hooks/useHomeCardsPreferences";
import { focusUniversalSearch } from "@/lib/universalSearch";
import { cn } from "@/lib/utils";

export default function Home() {
  const { user, isLoading: authLoading } = useAuth();
  const { prefs, setCard, anyEnabled } = useHomeCardsPreferences();
  const [quotes, setQuotes] = useState<QuoteType[]>(quotesService.getDefaultQuotes());
  const [authorFeedback, setAuthorFeedback] = useState<Record<string, "more" | "less">>({});

  const { data: authorPrefs } = useQuery({
    queryKey: ["author-preferences", user?.id],
    queryFn: () => quotesService.getAuthorPreferences(user!.id),
    enabled: !!user?.id && prefs.quote,
  });

  useEffect(() => {
    if (!user?.id || !prefs.quote) return;

    const userName = user?.username?.includes("@")
      ? user?.username.split("@")[0]
      : user?.username;
    contentPrefetchService.setUser(user.id, userName);
    contentPrefetchService.prefetchAll();

    const cachedBrain = contentPrefetchService.getCachedBrainContent();
    if (cachedBrain && cachedBrain.quotes.length > 0) {
      setQuotes(cachedBrain.quotes);
      contentPrefetchService.prefetchBrainContent(true);
    } else {
      quotesService.getPersonalizedQuotes(user.id, 5).then((personalizedQuotes) => {
        if (personalizedQuotes.length > 0) {
          setQuotes(personalizedQuotes);
        }
      });
    }
  }, [user?.id, user?.username, prefs.quote]);

  useEffect(() => {
    if (authorPrefs) {
      const feedback: Record<string, "more" | "less"> = {};
      authorPrefs.moreAuthors.forEach((a) => (feedback[a] = "more"));
      authorPrefs.lessAuthors.forEach((a) => (feedback[a] = "less"));
      setAuthorFeedback(feedback);
    }
  }, [authorPrefs]);

  if (authLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-4">
          <Brain className="w-12 h-12 mx-auto text-primary animate-pulse" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-4">
          <Brain className="w-12 h-12 mx-auto text-primary animate-pulse" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const hasMainCards =
    prefs.continue || prefs.brainBuilder || prefs.learningQueue || prefs.vital7;

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* DS EDIT 5aug26 — no separate Home header row: search + customize ride in the
          single chat header row (headerExtras below); search opens the pop-out universal search. */}
      {anyEnabled && (
        <section
          className={cn(
            "shrink-0 overflow-y-auto max-h-[40vh] border-b",
            "px-4 py-3 space-y-4"
          )}
        >
          {prefs.quote && user?.id && (
            <HomeQuoteCard
              quotes={quotes}
              userId={user.id}
              authorFeedback={authorFeedback}
              onAuthorFeedbackChange={setAuthorFeedback}
            />
          )}
          {prefs.gamification && <GamificationHub compact />}
          {hasMainCards && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 space-y-4">
                {prefs.continue && <ContinueWhereYouLeftOffCard />}
                {prefs.brainBuilder && <BrainBuilderWorkout />}
                {prefs.learningQueue && <LearningQueue />}
              </div>
              {prefs.vital7 && (
                <div className="space-y-4">
                  <Vital7Dashboard />
                </div>
              )}
            </div>
          )}
        </section>
      )}

      <div className="flex-1 min-h-0 overflow-hidden px-2 pb-2">
        <ChatWorkspace
          homeMode
          minimalEmptyState={!anyEnabled}
          className="h-full max-w-none"
          headerExtras={
            <>
              <Button
                variant="ghost"
                size="icon"
                className="relative flex-shrink-0 h-9 w-9 sm:h-8 sm:w-8"
                onClick={focusUniversalSearch}
                title="Search everything"
                aria-label="Search everything"
                data-testid="button-home-search"
              >
                <Search className="h-4 w-4" />
              </Button>
              <HomeCustomizeMenu prefs={prefs} onToggle={setCard} />
            </>
          }
        />
      </div>
    </div>
  );
}
